import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Instagram, Facebook, ArrowRight } from "lucide-react";
import { BRAND } from "@/data/mockData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast("You're on the list", { description: "We'll write only when it matters." });
      setEmail("");
    } catch {
      toast("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-[#0E0E0E] text-white/85 overflow-hidden">
      {/* Oversized brand watermark */}
      <div aria-hidden className="absolute inset-x-0 bottom-0 flex justify-center pointer-events-none select-none opacity-[0.06]">
        <span className="font-serif italic text-[38vw] leading-[0.75] tracking-[-0.05em] translate-y-[26%]">sirija</span>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          {/* Brand + newsletter */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <img src={BRAND.logo} alt="SIRIJA" className="h-11 w-11 rounded-full ring-1 ring-white/15" />
              <div className="font-serif text-3xl tracking-[0.14em]">SIRIJA</div>
            </div>
            <p className="mt-5 text-sm text-white/60 max-w-sm font-light leading-relaxed">
              Timeless silver — hand-finished in Pune and made to be lived in. Sign up for capsule launches and quiet studio notes.
            </p>
            <form onSubmit={subscribe} data-testid="footer-newsletter-form" className="mt-8 relative max-w-md">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="footer-newsletter-input"
                placeholder="your@email.com"
                className="h-12 pr-14 rounded-full bg-transparent border-white/20 text-white placeholder:text-white/40 focus-visible:ring-white/40"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="footer-newsletter-submit"
                aria-label="Subscribe"
                className="absolute right-1 top-1 h-10 w-10 rounded-full bg-white text-[#1A1A1A] flex items-center justify-center hover:bg-white/90 transition-colors disabled:opacity-60"
              >
                <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </form>

            <div className="mt-8 flex items-center gap-3">
              <a href={BRAND.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram" aria-label={`Instagram ${BRAND.instagramHandle}`}
                className="h-10 w-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 transition-colors">
                <Instagram className="h-4 w-4" strokeWidth={1.5} />
              </a>
              <a href={BRAND.facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook" aria-label="Facebook"
                className="h-10 w-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 transition-colors">
                <Facebook className="h-4 w-4" strokeWidth={1.5} />
              </a>
              <a href={BRAND.instagram} target="_blank" rel="noreferrer" className="text-xs text-white/50 hover:text-white/80 transition-colors">
                {BRAND.instagramHandle}
              </a>
            </div>
          </div>

          {/* Explore */}
          <div className="lg:col-span-2">
            <div className="overline text-white/50">Explore</div>
            <ul className="mt-5 space-y-3 text-sm text-white/70">
              <li><Link to="/collections" className="hover:text-white transition-colors">Collections</Link></li>
              <li><Link to="/shop" className="hover:text-white transition-colors">Shop</Link></li>
              <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
              <li><Link to="/blog" className="hover:text-white transition-colors">Journal</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <div className="overline text-white/50">Services</div>
            <ul className="mt-5 space-y-3 text-sm text-white/70">
              <li><Link to="/custom-orders" className="hover:text-white transition-colors">Custom Orders</Link></li>
              <li><Link to="/corporate-gifting" className="hover:text-white transition-colors">Corporate Gifting</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              <li><Link to="/wishlist" className="hover:text-white transition-colors">Wishlist</Link></li>
            </ul>
          </div>

          {/* Policies + address */}
          <div className="lg:col-span-3">
            <div className="overline text-white/50">Policies</div>
            <ul className="mt-5 space-y-3 text-sm text-white/70">
              <li><a className="hover:text-white transition-colors" href="#privacy">Privacy Policy</a></li>
              <li><a className="hover:text-white transition-colors" href="#refund">Refund Policy</a></li>
              <li><a className="hover:text-white transition-colors" href="#shipping">Shipping Policy</a></li>
              <li><a className="hover:text-white transition-colors" href="#terms">Terms &amp; Conditions</a></li>
            </ul>
            <div className="mt-8 text-xs text-white/40 leading-relaxed">
              {BRAND.address}
              <br />
              GSTIN: {BRAND.gstin}
              <br />
              <a href={`https://${BRAND.website}`} className="hover:text-white/70 transition-colors">{BRAND.website}</a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-white/40">
          <div>© {new Date().getFullYear()} SIRIJA. All rights reserved.</div>
          <div>Hand-crafted in Raviwar Peth, Pune.</div>
        </div>
      </div>
    </footer>
  );
}
